
import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Loading } from "../../../componentes/loading/loading";

/**
 * Props:
 *  - consolidadoAnual: objeto { [ano:number]: total:number | string } vindo da API
 *  - anosComparacao (opcional): [anoAtual, anoAnterior] para forçar anos específicos
 */
export default function DashboardBPConsolidado({ consolidadoAnual, anosComparacao, loading }) {
  // Coerção defensiva: BigDecimal pode chegar como string; convertemos para Number
  const parseNumber = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Determina os dois anos a exibir: usa anosComparacao ou pega os dois maiores do objeto
  const { anoAtual, anoAnterior } = useMemo(() => {
    if (Array.isArray(anosComparacao) && anosComparacao.length === 2) {
      return { anoAtual: anosComparacao[0], anoAnterior: anosComparacao[1] };
    }
    const anos = Object.keys(consolidadoAnual || {})
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => b - a); // desc
    return {
      anoAtual: anos[0],        // mais recente
      anoAnterior: anos[1] ?? (anos[0] ? anos[0] - 1 : undefined), // fallback
    };
  }, [consolidadoAnual, anosComparacao]);

  const totalAtual = parseNumber(consolidadoAnual?.[anoAtual]);
  const totalAnterior = parseNumber(consolidadoAnual?.[anoAnterior]);

  const series = [totalAtual, totalAnterior];

  const [isLoading, setIsLoading] = useState(true);

  const isEmpty =
    !series ||
    series.length === 0 ||
    series.every((v) => !Number.isFinite(v) || v === 0);

  useEffect(() => {
    const timeout = setTimeout(() => setIsLoading(false), 600); // loading leve
    return () => clearTimeout(timeout);
  }, [series]);

  const options = {
    chart: {
      type: "pie",
      height: 220,
      toolbar: { show: false },
    },
    labels: [
      anoAtual != null ? String(anoAtual) : "Atual",
      anoAnterior != null ? String(anoAnterior) : "Anterior",
    ],
    stroke: { width: 0 },
    legend: {
      position: "bottom",
      fontSize: "12px",
    },
    tooltip: {
      y: {
        formatter: (val) => "R$ " + Number(val).toLocaleString("pt-BR"),
      },
    },
    dataLabels: { enabled: false },
  };

  if (loading) {
    return <div className="w-[260px] h-[350px]">
      <Loading />
    </div>
  }

  return (
      <div className="w-[260px] h-[350px]">
      <h2 className="font-display text-base font-semibold mb-1 text-center text-foreground">
        Consolidado {anoAtual ?? ""} x {anoAnterior ?? ""}
      </h2>

      {isLoading && (
        <div className="flex justify-center h-[187px] items-center">
          <div className="animate-pulse bg-muted rounded-[50%] h-[164px] w-[164px]" />
        </div>
      )}

      {!isLoading && (
        <div className="animate-fade-in">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-[187px] text-muted-foreground">
              <i className="fa fa-exclamation-circle text-center text-[120px] opacity-80 mb-2" />
              <p className="text-sm">Não há valores para gerar este gráfico</p>
            </div>
          ) : (
            <Chart options={options} series={series} type="pie" height={220} />
          )}
        </div>
      )}

      <div className="text-xs text-foreground text-center mt-2">
        <p>
          <strong>Ano {anoAtual ?? "Atual"}:</strong>{" "}
          R$ {parseNumber(totalAtual).toLocaleString("pt-BR")}
        </p>
        <p>
          <strong>Ano {anoAnterior ?? "Anterior"}:</strong>{" "}
          R$ {parseNumber(totalAnterior).toLocaleString("pt-BR")}
        </p>
      </div>
    </div>
  );
}
